import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useBrand } from '../contexts/BrandContext';
import ConnectionProblemScreen from '../components/Common/ConnectionProblemScreen';
import { ArrowRightIcon, CheckIcon, CalendarIcon, DocumentIcon, UserGroupIcon, ClockIcon, CubeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, connectionProblem } = useUser();
  const { brand } = useBrand();
  const isSmbMate = brand.code === 'smbmate';

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  // 2026-10-23: a start-up check that cannot reach the server ends in Retry, not a spinner.
  if (loading && connectionProblem) return <ConnectionProblemScreen />;

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-blue-200">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if user is authenticated (they'll be redirected)
  if (isAuthenticated) {
    return null;
  }

  const features = [
    { title: 'Jobs & Scheduling', description: 'Schedule, assign, and track work from quote through completion.', icon: CalendarIcon },
    { title: 'Quotes, Invoices & Payments', description: 'Send branded documents, collect approvals and deposits, and get paid.', icon: DocumentIcon },
    { title: 'Customer Portal', description: 'Customers approve quotes, view jobs, pay invoices, and message your team.', icon: UserGroupIcon },
    { title: 'Time Tracking & Labor', description: 'Track job hours, review labor costs, and export timesheet data for payroll.', icon: ClockIcon },
    { title: 'Inventory & Purchasing', description: 'Manage items, stock locations, movements, vendors, and purchase orders.', icon: CubeIcon },
    { title: 'Two-Sided Marketplace', description: 'Find work or hire another service business from the same platform.', icon: GlobeAltIcon }
  ];

  const planComparison = [
    { capability: 'Assigned jobs & schedule', full: true, crew: true },
    { capability: 'Job time, photos & notes', full: true, crew: true },
    { capability: 'Quotes, invoices & payments', full: true, crew: false },
    { capability: 'Customer portal', full: true, crew: false },
    { capability: 'Inventory & purchasing', full: true, crew: false },
    { capability: 'Reports & analytics', full: true, crew: false },
    { capability: 'Two-sided marketplace', full: true, crew: false },
    { capability: 'Team & company settings', full: true, crew: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-3 py-3 text-center text-sm sm:text-base font-semibold">
        🚀 Now in beta — Start your 30-day free trial today.
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-blue-900/50 border-b border-blue-700/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="text-white font-bold text-lg sm:text-xl">{brand.name}</div>
          <div className="flex items-center gap-3 sm:gap-6">
            <a href="#features" className="hidden sm:inline text-blue-100 hover:text-white transition">Features</a>
            <a href="#pricing" className="hidden sm:inline text-blue-100 hover:text-white transition">Pricing</a>
            <button
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-blue-500 to-blue-400 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          {isSmbMate ? 'Manage Your Service Business in One App' : 'Manage Your Entire Trade Business in One App'}
        </h1>
        <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
          {isSmbMate
            ? 'All-in-one operations software for small service businesses.'
            : 'All-in-one field service software for contractors and trades.'}
          {' '}
          Quote, schedule, invoice, and get paid — with zero complexity.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <button
            onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-blue-500 to-blue-400 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-xl transition flex items-center justify-center gap-2"
          >
            Start Free 30-Day Trial <ArrowRightIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="border-2 border-blue-300 text-blue-100 px-8 py-3 rounded-lg font-semibold hover:bg-blue-800/50 transition"
          >
            See Features
          </button>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {['🧾 Quotes', '🗓 Scheduling', '💸 Invoices', '📈 Reports'].map((pill) => (
            <div key={pill} className="bg-blue-800/50 border border-blue-600 text-blue-100 px-4 py-2 rounded-full text-sm font-medium">
              {pill}
            </div>
          ))}
        </div>
        <p className="text-blue-200">
          <strong>Full Access $25</strong> / user / month · <strong>Crew $5</strong> / user / month
        </p>
      </div>

      {/* Features Section */}
      <div id="features" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-white text-center mb-12">
          Everything You Need — One Connected Platform
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="bg-blue-800/30 border border-blue-600/50 rounded-lg p-6 hover:bg-blue-800/50 transition hover:scale-105"
            >
              <div className="flex items-center gap-3 mb-3">
                <feature.icon className="w-8 h-8 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
              </div>
              <p className="text-blue-200">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Plan Comparison Section */}
      <div id="compare" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-white text-center mb-12">
          The Right Access for Every Role
        </h2>
        <p className="text-blue-100 text-center max-w-3xl mx-auto -mt-6 mb-10">
          Owners and office staff get the complete business toolkit. Crew members get a focused mobile workspace for the jobs they need to complete.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-blue-600">
                <th className="text-white font-semibold py-3 px-4">Capability</th>
                <th className="text-white font-semibold py-3 px-4 text-center">Full Access · $25</th>
                <th className="text-white font-semibold py-3 px-4 text-center">Crew · $5</th>
              </tr>
            </thead>
            <tbody>
              {planComparison.map((row) => (
                <tr key={row.capability} className="border-b border-blue-700/30 hover:bg-blue-800/20">
                  <td className="text-blue-100 py-3 px-4">{row.capability}</td>
                  <td className="text-center py-3 px-4 text-blue-100">
                    {row.full ? <span className="inline-flex items-center gap-1"><CheckIcon className="w-4 h-4" /> Included</span> : '—'}
                  </td>
                  <td className="text-center py-3 px-4 text-blue-100">
                    {row.crew ? <span className="inline-flex items-center gap-1"><CheckIcon className="w-4 h-4" /> Included</span> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-blue-300 text-center mt-5">
          Customers and vendors are not billable seats.
        </p>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-white text-center mb-12">
          Simple, Fair Pricing
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-blue-800/30 border border-blue-600/50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Full Access</h3>
            <p className="text-4xl font-bold text-blue-400 mb-4">$25<span className="text-lg text-blue-200">/user/mo</span></p>
            <p className="text-blue-200 mb-6">Complete business-management access for owners and office staff.</p>
            <ul className="space-y-2 text-blue-100 mb-6">
              <li>✅ Quotes & Invoicing</li>
              <li>✅ Scheduling</li>
              <li>✅ Customer Portal</li>
              <li>✅ Time Tracking & Labor</li>
              <li>✅ Inventory & Purchasing</li>
              <li>✅ Chat & Messaging</li>
              <li>✅ Analytics & Reports</li>
              <li>✅ Two-Sided Marketplace</li>
            </ul>
            <button
              onClick={() => navigate('/signup')}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-400 text-white py-3 rounded-lg font-semibold hover:shadow-xl transition"
            >
              Start Free 30-Day Trial
            </button>
          </div>
          <div className="bg-blue-800/30 border border-blue-600/50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Crew</h3>
            <p className="text-4xl font-bold text-blue-400 mb-4">$5<span className="text-lg text-blue-200">/user/mo</span></p>
            <p className="text-blue-200 mb-6">Focused access for technicians, apprentices, and helpers.</p>
            <ul className="space-y-2 text-blue-100 mb-6">
              <li>✅ Assigned Jobs & Schedule</li>
              <li>✅ Job Time Tracking</li>
              <li>✅ Job Photos & Notes</li>
              <li>✅ Mobile Access</li>
              <li>— Quotes & Invoicing</li>
              <li>— Inventory & Purchasing</li>
              <li>— Analytics & Reports</li>
              <li>— Marketplace Access</li>
            </ul>
            <button
              onClick={() => navigate('/signup')}
              className="w-full border-2 border-blue-400 text-blue-100 py-3 rounded-lg font-semibold hover:bg-blue-800/50 transition"
            >
              Start Free 30-Day Trial
            </button>
          </div>
        </div>
      </div>

      {/* Beta Signup Section */}
      <div id="beta" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center relative">
        {/* Decorative floating orbs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float-slow"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float-delayed"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float-reverse"></div>
        
        <h2 className="text-4xl font-bold text-white mb-6 relative z-10">Join Our Beta</h2>
        <p className="text-xl text-blue-100 mb-12 relative z-10">
          {isSmbMate
            ? 'Be among the first small service businesses to simplify your operations.'
            : 'Be among the first trades and field service professionals to simplify your operations.'}
        </p>
        
        {/* CTA Button */}
        <div className="mb-8 relative z-10">
          <button
            onClick={() => document.getElementById('zoho-form')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-blue-500 to-blue-400 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-xl transition inline-flex items-center gap-2"
          >
            Get Early Access <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
        
        {/* Glassmorphic container with gradient border */}
        <div className="relative z-10">
          {/* Outer glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400 rounded-2xl opacity-50 blur-xl animate-pulse-slow"></div>
          
          {/* Main glassmorphic card */}
          <div className="relative bg-gradient-to-br from-blue-900/80 via-blue-800/70 to-indigo-900/80 backdrop-blur-xl rounded-2xl p-1 shadow-2xl">
            {/* Inner gradient border effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/50 via-cyan-400/50 to-indigo-400/50 rounded-2xl opacity-50"></div>
            
            {/* Content container */}
            <div className="relative bg-white/95 backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-2xl">
              {/* Decorative gradient accent at top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400 rounded-t-xl"></div>
              
              {/* Glass header accent */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800 font-medium">
                  🚀 Start your 30-day free trial — no credit card required!
                </p>
              </div>
              
              <iframe
                id="zoho-form"
                title="Zoho Beta Signup Form"
                aria-label="Contact Us"
                src="https://forms.zohopublic.com/jerrytrades1/form/TradesmateContact/formperma/3_LWnNa1_dV_0do8T5opf-lXXRp8RSM2032XcT68dCY"
                style={{ width: '100%', height: '600px', border: 'none' }}
                className="rounded-lg"
              />
              
              {/* Decorative gradient accent at bottom */}
              <div className="mt-4 h-1 bg-gradient-to-r from-indigo-400 via-cyan-400 to-blue-400 rounded-b-lg"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-blue-700/30 bg-blue-900/50 py-8 text-center text-blue-300">
        <p>{brand.name} © {new Date().getFullYear()} | {brand.tagline}</p>
        {/* Legal operating entity, stated explicitly. Twilio/carrier compliance
            verification (error 18601) checks that the business name on the profile
            can be tied to the website; the brand name alone is not enough, and the
            registered name must appear character-for-character. */}
        {/* Removed 2026-08-05: "Parent company of Kentrix Technologies". It was a
            routing artefact and stated the hierarchy backwards — CGRenewables LLC is
            the parent of BOTH TradesMatePro and Kentrix Technologies, not
            TradesMatePro of Kentrix. A verifier checking "does tradesmatepro.com
            belong to CGRenewables LLC?" was instead finding a conflicting corporate
            claim, which is a plausible contributor to the 18601 rejection. */}
        <p className="mt-1 text-sm text-blue-300">
          {brand.name} is a product of <span className="font-medium text-blue-200">CGRenewables LLC</span>
        </p>
        <p className="mt-2 text-sm">
          <a href="/privacy.html" className="hover:text-white transition">Privacy Policy</a> |
          <a href="/terms.html" className="hover:text-white transition"> Terms of Service</a> |
          <a href="/data-deletion.html" className="hover:text-white transition"> Data Deletion</a> |
          <a href="mailto:jerry@tradesmatepro.com" className="hover:text-white transition"> Contact CGRenewables</a>
        </p>
      </div>
    </div>
  );
};

export default Landing;

